import { useState } from "react";
import { Calculator, Check, Plus, X } from "lucide-react";

import { floorArea, wallArea, type Opening, type Rect } from "@/lib/area";
import { BTN_GHOST, BTN_PRIMARY, Label } from "./ui";

const NUM = "field num w-full";
const n = (v: string) => (v === "" ? 0 : Number(v));

/**
 * Inline surface calculator. Floor: rectangles (length × width) added up, so
 * an L-shaped room is two rows. Wall: perimeter (from the room's length and
 * width, or typed) × height, minus doors and windows. `onApply` hands the m²
 * back to the section; without it the calculator is a standalone tool.
 */
export function AreaCalculator({ kind, onApply, onClose }: { kind: "floor" | "wall"; onApply?: (sqm: number) => void; onClose?: () => void }) {
  const [rects, setRects] = useState<Rect[]>([{ length: 0, width: 0 }]);
  const [room, setRoom] = useState({ length: 0, width: 0, perimeter: "" as string, height: 2.8 });
  const [openings, setOpenings] = useState<Opening[]>([{ label: "Door", width: 0.9, height: 2.1, count: 1 }]);

  const floor = floorArea(rects);
  const wall = wallArea({ length: room.length, width: room.width, perimeter: room.perimeter === "" ? null : n(room.perimeter), height: room.height, openings });
  const result = kind === "floor" ? floor : wall.net;

  return (
    <div className="mt-2 rounded-xl border border-white/12 bg-night/50 p-3 text-sm">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.16em] text-ice/70">
          <Calculator className="h-3.5 w-3.5" /> {kind === "floor" ? "Floor area" : "Wall area"}
        </span>
        {onClose && (
          <button type="button" className="text-ice/50 hover:text-paper" onClick={onClose} aria-label="Close calculator">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {kind === "floor" ? (
        <div className="mt-2 space-y-2">
          {rects.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto_1fr_auto_auto] items-center gap-2">
              <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" placeholder="length m" value={r.length || ""} onChange={(e) => setRects(rects.map((x, k) => (k === i ? { ...x, length: n(e.target.value) } : x)))} />
              <span className="text-ice/50">×</span>
              <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" placeholder="width m" value={r.width || ""} onChange={(e) => setRects(rects.map((x, k) => (k === i ? { ...x, width: n(e.target.value) } : x)))} />
              <span className="num w-20 text-right text-ice/80">{floorArea([r]).toFixed(2)}</span>
              <button type="button" className="text-ice/40 hover:text-pink disabled:opacity-30" disabled={rects.length === 1} onClick={() => setRects(rects.filter((_, k) => k !== i))} aria-label="Remove rectangle">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <button type="button" className="text-xs text-ice/70 hover:text-paper" onClick={() => setRects([...rects, { length: 0, width: 0 }])}>
            <Plus className="mr-1 inline h-3 w-3" /> Add a rectangle (L-shaped rooms, alcoves)
          </button>
        </div>
      ) : (
        <div className="mt-2 space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className="block">
              <Label>Room length m</Label>
              <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" value={room.length || ""} onChange={(e) => setRoom({ ...room, length: n(e.target.value) })} />
            </label>
            <label className="block">
              <Label>Room width m</Label>
              <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" value={room.width || ""} onChange={(e) => setRoom({ ...room, width: n(e.target.value) })} />
            </label>
            <label className="block">
              <Label>or perimeter m</Label>
              <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" placeholder={wall.perimeter ? String(wall.perimeter) : "auto"} value={room.perimeter} onChange={(e) => setRoom({ ...room, perimeter: e.target.value })} />
            </label>
            <label className="block">
              <Label>Height m</Label>
              <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" value={room.height || ""} onChange={(e) => setRoom({ ...room, height: n(e.target.value) })} />
            </label>
          </div>
          <div>
            <Label>Doors &amp; windows to subtract</Label>
            <div className="space-y-2">
              {openings.map((o, i) => (
                <div key={i} className="grid grid-cols-[1fr_4.5rem_auto_4.5rem_auto_3.5rem_auto] items-center gap-1.5">
                  <input className="field" placeholder="Door, window…" value={o.label} onChange={(e) => setOpenings(openings.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} />
                  <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" value={o.width || ""} onChange={(e) => setOpenings(openings.map((x, k) => (k === i ? { ...x, width: n(e.target.value) } : x)))} />
                  <span className="text-ice/50">×</span>
                  <input className={NUM} type="number" inputMode="decimal" min={0} step="0.01" dir="ltr" value={o.height || ""} onChange={(e) => setOpenings(openings.map((x, k) => (k === i ? { ...x, height: n(e.target.value) } : x)))} />
                  <span className="text-ice/50">×</span>
                  <input className={NUM} type="number" inputMode="numeric" min={0} step="1" dir="ltr" value={o.count || ""} onChange={(e) => setOpenings(openings.map((x, k) => (k === i ? { ...x, count: n(e.target.value) } : x)))} />
                  <button type="button" className="text-ice/40 hover:text-pink" onClick={() => setOpenings(openings.filter((_, k) => k !== i))} aria-label="Remove opening">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" className="text-xs text-ice/70 hover:text-paper" onClick={() => setOpenings([...openings, { label: "Window", width: 1.2, height: 1.2, count: 1 }])}>
                <Plus className="mr-1 inline h-3 w-3" /> Add door / window
              </button>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-xs text-ice/70">
            <span>Perimeter <b className="num text-paper">{wall.perimeter.toFixed(2)} m</b></span>
            <span>Gross <b className="num text-paper">{wall.gross.toFixed(2)} m²</b></span>
            <span>Openings <b className="num text-paper">− {wall.openings.toFixed(2)} m²</b></span>
          </div>
        </div>
      )}

      <div className="mt-3 flex items-center justify-between border-t border-white/10 pt-2">
        <span className="text-ice/70">
          {kind === "floor" ? "Floor" : "Net wall"} area <b className="num text-lg text-goldbright">{result.toFixed(2)} m²</b>
        </span>
        {onApply && (
          <button type="button" className={BTN_PRIMARY} disabled={result <= 0} onClick={() => onApply(result)}>
            <Check className="h-3.5 w-3.5" /> Use {result.toFixed(2)} m²
          </button>
        )}
      </div>
    </div>
  );
}

/** Toggle button + calculator, for the Area field of a section. */
export function AreaCalculatorToggle({ kind, onApply, disabled }: { kind: "floor" | "wall"; onApply: (sqm: number) => void; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`${BTN_GHOST} px-3 py-1`} disabled={disabled} onClick={(e) => { e.preventDefault(); setOpen((o) => !o); }}>
        <Calculator className="h-3.5 w-3.5" /> {open ? "Hide calculator" : "Calculate m²"}
      </button>
      {open && (
        <AreaCalculator
          kind={kind}
          onClose={() => setOpen(false)}
          onApply={(sqm) => {
            onApply(sqm);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}
